import { Close as CloseIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import DashboardContent from "components/layout/DashboardContent";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  useRefreshCachesMutation,
  useRetrieveCacheStatisticsQuery,
  useTriggerCreateTableMutation,
  useTriggerMaterializationMutation,
} from "services/materializeService";

const ErrorBlock = ({ label, setDisplayError, errors }) => {
  if (errors) {
    let lastError = null;
    let noErrorCount = 0;
    for (const ind in errors) {
      const e = errors[ind];
      if (!e) {
        noErrorCount += 1;
      } else {
        lastError = e;
        noErrorCount = 0;
      }
    }
    const showFullError = (data) => {
      setDisplayError({
        title: label,
        message: data
          .split("\\n")
          .map((l) =>
            l.match(/(.{1,90}(.{0,30}?[\s,):/]|.{0,30}))/g).join("\n")
          )
          .join("\n"),
      });
    };
    if (lastError) {
      const pair = lastError.split(";", 2);
      if (pair.length === 2) {
        const longMsg = pair[1].length > 100;
        let msg = pair[1];
        if (longMsg) {
          msg =
            pair[1].substring(0, 45) +
            " ... " +
            pair[1].substring(pair[1].length - 45);
        }
        return (
          <>
            <TableRow>
              <TableCell padding={"none"}></TableCell>
              <TableCell padding={"none"}>{"Last Errored at"}</TableCell>
              <TableCell colSpan={4}>{pair[0]}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell padding={"none"}>
                {longMsg && (
                  <Button onClick={() => showFullError(pair[1])}>
                    {"Show full error"}
                  </Button>
                )}
              </TableCell>
              <TableCell padding={"none"}>{"Last Error"}</TableCell>
              <TableCell colSpan={4}>{msg}</TableCell>
            </TableRow>
            {noErrorCount > 0 && (
              <TableRow>
                <TableCell padding={"none"}></TableCell>
                <TableCell padding={"none"}>{"Followed by "}</TableCell>
                <TableCell colSpan={4}>
                  {noErrorCount}
                  {" Successful executions"}
                </TableCell>
              </TableRow>
            )}
          </>
        );
      }
    }
  }
  return <></>;
};

const TimingBlock = ({ label, value }) => {
  return (
    <>
      <TableRow>
        <TableCell colSpan={6}>
          <b>{label}</b>
          {": "}
          {descriptionForQuery(label)}
        </TableCell>
        <TableCell> </TableCell>
      </TableRow>
      <TableRow>
        <TableCell padding={"none"}></TableCell>
        <TableCell padding={"none"}>{"Min time seconds"}</TableCell>
        <TableCell padding={"none"}>{value.minTime}</TableCell>
        <TableCell padding={"none"} rowSpan={4}>
          <ResponsiveContainer width={400} height={200}>
            <BarChart
              data={value.data.map((d, i) => ({ index: i, value: d }))}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray={"3 3"} />
              <XAxis dataKey={"index"} name={"Index"} />
              <YAxis
                dataKey={"value"}
                name={"Value"}
                scale={"log"}
                domain={["auto", "auto"]}
                allowDataOverflow={false}
                label={{
                  value: "Time seconds",
                  angle: -90,
                  position: "left",
                  offset: -10,
                }}
              />
              <Tooltip
                formatter={(value) => {
                  if (value > 0.9) return [`${value.toFixed(3)} s`, "Time"];
                  else return [`${(value * 1000).toFixed(1)} ms`, "Time"];
                }}
              />

              <Bar dataKey={"value"} fill={"#8884d8"} name={"Value"} />
            </BarChart>
          </ResponsiveContainer>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell padding={"none"}></TableCell>
        <TableCell padding={"none"}>{"Max time seconds"}</TableCell>
        <TableCell padding={"none"}>{value.maxTime}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell padding={"none"}></TableCell>
        <TableCell padding={"none"}>{"Avg time seconds"}</TableCell>
        <TableCell padding={"none"}>{value.avgTime}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell padding={"none"}></TableCell>
        <TableCell padding={"none"}>{"Last run"}</TableCell>
        <TableCell padding={"none"}>{value.lastUsed}</TableCell>
      </TableRow>
    </>
  );
};

const descriptionForQuery = (dataName) => {
  switch (dataName) {
    case "cachedQuery":
      return "Time, in materialise, to perform the query on cached data";
    case "baseQuery":
      return "Time, in materialise, to perform the query directly on Athena";
    case "incrementalQuery":
      return "Time, in materialise, to perform an incremental update of the cache";
    case "listSubmissionsEndPoint":
      return (
        "Time to read and process the list_submissions end point in " +
        "the submissions service"
      );
    case "overallQuerySubmissionsLocallyCached":
      return (
        "Time to return data " +
        "in the query-submissions end point of the submissions service when " +
        "the data is cached in the submissions service"
      );
    case "overallQuerySubmissionsNotCached":
      return (
        "Time to return data " +
        "in the query-submissions end point of the submissions service when " +
        "the data is NOT cached so is cached in this call in the submissions service"
      );
    case "overallReviewReport":
      return (
        "Time to return data " +
        "in the query-review-report end point of the reports_v2 service."
      );
    case "overallReviewReportNotCached":
      return (
        "Time to return data " +
        "in the query-review-report end point of the submissions service when " +
        "the data is NOT cached so is cached in this call in the submissions service"
      );
    case "overallListSubmissions":
      return (
        "Time to return data " +
        "in the list-submissions end point of the report_v2 service."
      );
    default:
      return "";
  }
};

const RecacheQueryButton = ({ companyId, queryName, onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [triggerRefresh] = useRefreshCachesMutation();

  return (
    <Button
      onClick={async () => {
        try {
          setIsLoading(true);
          await triggerRefresh({ impersonateCompanyId: companyId, queryName });
          onComplete();
        } finally {
          setIsLoading(false);
        }
      }}
      isLoading={isLoading}
    >
      {"Recache query"}
    </Button>
  );
};

const ReinitConfirmDialog = ({ open, onConfirm, onCancel }) => (
  <Dialog open={open} onClose={onCancel} maxWidth={"xs"} fullWidth>
    <DialogTitle>{"Are you sure?"}</DialogTitle>
    <DialogContent>
      {
        "This action can take some time, during which users will not be able to query submissions, view data in the dashboards, or run reports"
      }
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
      <Button onClick={onCancel} color={"secondary"}>
        {"Cancel"}
      </Button>
      <Button onClick={onConfirm}>{"Confirm"}</Button>
    </DialogActions>
  </Dialog>
);

// Full company re-init: recreate Athena tables + re-materialize all data (superuser).
const ReinitControls = ({ companyId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [
    triggerCreateTable,
    { isLoading: creatingTables },
  ] = useTriggerCreateTableMutation();
  const [triggerMaterialization] = useTriggerMaterializationMutation();
  const [materializing, setMaterializing] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const run = async (mutation, label) => {
    try {
      await mutation({ companyId }).unwrap();
      enqueueSnackbar(`${label} completed`, { variant: "success" });
    } catch (error) {
      const message =
        error?.data?.message || error?.message || "see CloudWatch logs";
      enqueueSnackbar(`${label} failed: ${message}`, {
        variant: "error",
        autoHideDuration: 12000,
      });
    }
  };

  // Re-materialize is long-running: Fire-and-forget, report it started, and block the button
  // for a cooldown so it isn't re-triggered while it runs.
  const startMaterialization = () => {
    triggerMaterialization({ companyId });
    enqueueSnackbar(
      "Materialization started — runs in the background, may take a few minutes.",
      { variant: "info" }
    );
    setMaterializing(true);
    setTimeout(() => setMaterializing(false), 60000);
  };

  const handleConfirm = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action === "recreate") run(triggerCreateTable, "Recreate tables");
    else if (action === "materialize") startMaterialization();
  };

  return (
    <>
      <ReinitConfirmDialog
        open={pendingAction != null}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
      <Stack direction={"row"} gap={1}>
        <Button
          onClick={() => setPendingAction("recreate")}
          isLoading={creatingTables}
          isDisabled={materializing}
          size={"small"}
        >
          {"Recreate tables"}
        </Button>
        <Button
          color={"secondary"}
          onClick={() => setPendingAction("materialize")}
          isLoading={materializing}
          isDisabled={creatingTables}
          size={"small"}
        >
          {"Re-materialize all data"}
        </Button>
      </Stack>
    </>
  );
};

const QueryCache = () => {
  const { companyId } = useParams();
  const {
    data: cacheStats,
    status: cacheStatsStatus,
    refetch: refetchCacheStats,
  } = useRetrieveCacheStatisticsQuery({ impersonateCompanyId: companyId });

  const [displaySql, setDisplaySql] = useState(null);
  const [displayError, setDisplayError] = useState(null);
  const selectedCaches = cacheStats?.caches
    ? cacheStats?.caches.filter((cs) => cs.queryName === displaySql)
    : null;
  const selectedCache = selectedCaches?.length > 0 ? selectedCaches[0] : null;

  if (cacheStatsStatus !== "fulfilled") {
    return (
      <AdminDashboard>
        <DashboardContent isFullWidth={false}>
          <Grid item xs={12}>
            <Component
              title={"Query Caches"}
              options={<ReinitControls companyId={companyId} />}
            >
              <BigLoader />
            </Component>
          </Grid>
        </DashboardContent>
      </AdminDashboard>
    );
  }
  return (
    <AdminDashboard>
      <DashboardContent isFullWidth={false}>
        <Grid item xs={12}>
          <Component
            title={"Query Caches"}
            options={<ReinitControls companyId={companyId} />}
          >
            <Dialog
              open={displayError != null}
              onClose={() => setDisplayError(null)}
              fullWidth={true}
              maxWidth={true}
            >
              <DialogTitle>
                {"Error Message for "}
                {displayError?.["title"]}
              </DialogTitle>
              <IconButton
                onClick={() => setDisplayError(null)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent>
                <pre>{displayError?.["message"]}</pre>
              </DialogContent>
            </Dialog>
            <Dialog
              open={displaySql != null}
              onClose={() => setDisplaySql(null)}
              fullWidth={true}
              maxWidth={true}
            >
              <DialogTitle>
                {"Query for"}
                {displaySql}
              </DialogTitle>
              <IconButton
                onClick={() => setDisplaySql(null)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent>
                <pre>{selectedCache?.query ?? "Not available"}</pre>
              </DialogContent>
            </Dialog>
            <Stack spacing={2} alignItems={"center"}>
              {cacheStats.caches.map((cache, index) => (
                <Table key={index} sx={{ width: "80hw" }}>
                  <TableBody>
                    <TableRow>
                      <TableCell>{"Name: "}</TableCell>
                      <TableCell>
                        {" "}
                        <b>{cache.queryName}</b>{" "}
                      </TableCell>
                      <TableCell colSpan={2}>
                        <Stack direction={"row"} spacing={5}>
                          <Button onClick={() => refetchCacheStats()}>
                            {"Refresh"}
                          </Button>
                          <RecacheQueryButton
                            companyId={companyId}
                            queryName={cache.queryName}
                            onComplete={refetchCacheStats}
                          />
                          <Button
                            onClick={() => setDisplaySql(cache.queryName)}
                          >
                            {"Display query"}
                          </Button>
                        </Stack>
                      </TableCell>
                      <TableCell> </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{"caching enabled: "}</TableCell>
                      <TableCell colSpan={3}>
                        {cache.cachingDisabled ? "false" : "true"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{"Cache exists: "}</TableCell>
                      <TableCell colSpan={3}>
                        {cache.cacheExists ? "true" : "false"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{"Last recache: "}</TableCell>
                      <TableCell colSpan={3}>{cache.lastUpdated}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{"Updates stored: "}</TableCell>
                      <TableCell colSpan={3}>
                        {cache.activeUpdateCount}
                      </TableCell>
                    </TableRow>
                    {Object.keys(cache.timing)
                      .sort()
                      .map((key) => {
                        const timing = cache.timing[key];
                        const errors = cache.errors?.[key];
                        return (
                          <>
                            <TimingBlock label={key} value={timing} />
                            <ErrorBlock
                              label={key}
                              setDisplayError={setDisplayError}
                              errors={errors}
                            />
                          </>
                        );
                      })}
                  </TableBody>
                </Table>
              ))}
            </Stack>
          </Component>
        </Grid>
      </DashboardContent>
    </AdminDashboard>
  );
};

export default QueryCache;
